import CloseIcon from '@mui/icons-material/Close';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { Box, Grid, IconButton, List, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MdOutlineLocationOn } from 'react-icons/md';
import { csrfTokenCall } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsLocationSearch,
  UserRoundIcon,
  TagsIcon,
} from 'wisestep-base';

import { ContactAutocomplete } from '../../../components/ContactAutocomplete';
import {
  CreatedBwDatesFilter,
  WsAccordion,
  WsTagsFilter,
} from '../../../components/filters/components';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { leadFilterDateOptions } from '../../../features/leads/helper/leadHelper';
import { IFilters, contactsDefaultFilterValues } from '../helper/ContactListingHelper';

interface IContactsAdvanceFilterProps {
  onClose: () => void;
  onSubmit: (fields: IFilters) => void;
  defaultValues: IFilters;
  tags: string[];
}

const StyledGrid = styled(Grid)(() => ({
  padding: 1,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const ContactsAdvanceFilters = ({
  onClose,
  onSubmit,
  defaultValues,
  tags,
}: IContactsAdvanceFilterProps) => {
  const methods = useForm<IFilters>({
    defaultValues: defaultValues,
  });
  const { handleSubmit, reset, getValues, watch } = methods;
  const [resetKey, setResetKey] = useState(0);
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');

  useEffect(() => {
    reset(defaultValues);
    setResetKey((prev) => prev + 1);
  }, [defaultValues, reset]);

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  const handleReset = () => {
    reset();
    onSubmit(contactsDefaultFilterValues);
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        onReset={handleReset}
        bgcolor={WsColors.White}
        borderRadius={2}
      >
        <StyledGrid pl={2}>
          <WsTypography variant={WsTypoVariants.TITLE} bold>
            Advanced Filters
          </WsTypography>
          <WsButton variant="contained" size="small" type="submit">
            Apply
          </WsButton>
          <IconButton type="reset">
            <RestartAltOutlinedIcon color="error" />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon color="disabled" />
          </IconButton>
        </StyledGrid>
        <Box p={1} ml={1}>
          <WsTypography color={WsTypoColors.PRIMARY} variant={WsTypoVariants.SUB_TITLE} bold>
            Contacts
          </WsTypography>
        </Box>
        <List>
          <CreatedBwDatesFilter
            key={resetKey}
            title="Created on or between"
            name="fromDate"
            name1="toDate"
            selectStatus="filterByDateType"
            disableFuture={false}
            showAccordionIcon
            isSubmittedOrUpdated
            selectArray={leadFilterDateOptions}
          />
          <WsAccordion
            title="Created By"
            titleColor={WsTypoColors.GREYTEXT}
            startIcon={<UserRoundIcon />}
          >
            <RecruiterSuggestions
              name="createdByIds"
              multiple={true}
              defaultValue={getValues('createdByIds')}
              value={watch('createdByIds')}
            />
          </WsAccordion>
          <WsAccordion
            title="Contact Location"
            titleColor={WsTypoColors.GREYTEXT}
            startIcon={<MdOutlineLocationOn color={WsColors.TertiaryShades800} size={20} />}
          >
            <WsLocationSearch
              name="location"
              multiSelect={false}
              wisestepApi={true}
              token={token}
              authorization={auth}
              size="small"
              placeHolder=""
            />
          </WsAccordion>
          <WsAccordion
            title="Supervisor"
            titleColor={WsTypoColors.GREYTEXT}
            startIcon={<UserRoundIcon />}
          >
            <ContactAutocomplete name="supervisors" multiple />
          </WsAccordion>
          <WsAccordion title="Tags" titleColor={WsTypoColors.GREYTEXT} startIcon={<TagsIcon />}>
            <WsTagsFilter name="tags" defaultValue={defaultValues.tags} label="" options={tags} />
          </WsAccordion>
        </List>
      </Box>
    </FormProvider>
  );
};

export default ContactsAdvanceFilters;
